import { Layout, Dropdown, Avatar, Badge, Space, Tooltip } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { useAuthStore } from '../stores/authStore'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const { Header: AntHeader } = Layout

interface HeaderProps {
  collapsed: boolean
  onToggleCollapsed: () => void
}

const Header = ({ collapsed, onToggleCollapsed }: HeaderProps) => {
  const { t } = useTranslation()
  const { user, logout } = useAuthStore()

  // 用户菜单项
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('header.profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('header.settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth.logout'),
      danger: true,
    },
  ]

  // 处理用户菜单点击
  const handleUserMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'profile':
        // 跳转到个人资料页面
        break
      case 'settings':
        // 跳转到设置页面
        break
      case 'logout':
        logout()
        break
    }
  }

  // 通知菜单项
  const notificationItems = [
    {
      key: '1',
      label: t('header.notification.comment'),
      description: t('header.notification.commentDesc'),
      time: '5分钟前',
    },
    {
      key: '2',
      label: t('header.notification.maintenance'),
      description: t('header.notification.maintenanceDesc'),
      time: '2小时前',
    },
    {
      key: '3',
      label: t('header.notification.welcome'),
      description: t('header.notification.welcomeDesc'),
      time: '1天前',
    },
  ]

  return (
    <AntHeader
      style={{
        padding: '0 24px',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
        zIndex: 1,
      }}
    >
      {/* 左侧：折叠按钮和面包屑 */}
      <div className="flex items-center">
        <Tooltip title={collapsed ? t('header.expandMenu') : t('header.collapseMenu')}>
          <div
            className="text-lg cursor-pointer p-2 hover:bg-gray-100 rounded"
            onClick={onToggleCollapsed}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
        </Tooltip>

        {/* 面包屑导航（后续实现） */}
        <div className="ml-4 text-gray-600">
          <span className="font-medium">NovelAI</span>
          <span className="mx-2">/</span>
          <span>{t('header.adminPanel')}</span>
        </div>
      </div>

      {/* 右侧：操作按钮和用户信息 */}
      <Space size="large">
        {/* 积分按钮 */}
        <Tooltip title="积分中心">
          <Link
            to="/credits"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-yellow-600 bg-yellow-50 hover:bg-yellow-100 hover:text-yellow-700 transition-all duration-200"
          >
            <ThunderboltOutlined className="text-yellow-500" />
            <span className="hidden sm:inline">积分</span>
          </Link>
        </Tooltip>

        {/* 帮助按钮 */}
        <Tooltip title={t('header.helpDocs')}>
          <QuestionCircleOutlined className="text-lg cursor-pointer hover:text-primary" />
        </Tooltip>

        {/* 通知 */}
        <Dropdown
          menu={{
            items: notificationItems.map(item => ({
              key: item.key,
              label: (
                <div className="min-w-[200px]">
                  <div className="font-medium">{item.label}</div>
                  <div className="text-gray-500 text-sm">{item.description}</div>
                  <div className="text-gray-400 text-xs mt-1">{item.time}</div>
                </div>
              ),
            })),
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Badge count={3} size="small">
            <BellOutlined className="text-lg cursor-pointer hover:text-primary" />
          </Badge>
        </Dropdown>

        {/* 用户信息 */}
        <Dropdown
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div className="flex items-center cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
            <Avatar
              size="default"
              icon={<UserOutlined />}
              src={user?.avatar}
              className="bg-gradient-primary"
            />
            <div className="ml-2 hidden md:block">
              <div className="font-medium">{user?.username || t('header.user')}</div>
              <div className="text-gray-500 text-xs capitalize">
                {user?.role || 'reader'}
              </div>
            </div>
          </div>
        </Dropdown>
      </Space>
    </AntHeader>
  )
}

export default Header
